/**
 * Sitewide add-to-cart enhancement for product cards (search results,
 * collection grids, featured collections — anywhere card-product.liquid
 * renders with quick_add: 'standard' on a single-variant product).
 *
 * Dawn's own product-form.js redirects the whole page to /cart after a
 * successful add if it can't confirm a cart notification/drawer element.
 * Rather than depend on that detection, this script intercepts the form
 * submit in the CAPTURE phase at the document level — before Dawn's own
 * listener on the form itself — and runs its own complete add-to-cart
 * flow. Dawn's handler never runs for these forms, so it can never
 * redirect.
 *
 * IMPORTANT: <cart-notification> and <cart-drawer> do NOT listen for the
 * PUB_SUB_EVENTS.cartUpdate pub/sub event — publishing it alone will
 * never open or refresh them. They only update when something calls
 * their `.renderContents(data)` method directly, and that data must
 * contain a `sections` object (pre-rendered section HTML), which Shopify
 * only returns if the request explicitly asks for it via a `sections`
 * param — exactly what Dawn's own product-form.js does. This script
 * replicates that: it appends `sections`/`sections_url` to the request,
 * then calls `cartElement.renderContents(data)` on success. It still
 * also publishes PUB_SUB_EVENTS.cartUpdate afterward, since other
 * elements (cart-items, quick-add-bulk) DO rely on that event.
 *
 * DOM structure produced by snippets/card-product.liquid:
 * <div class="search-atc-wrapper" data-search-atc-variant="...">
 *   <product-form>...button.quick-add__submit (arrow + label)...</product-form>
 *   <div class="search-atc-qty" hidden>...stepper...</div>
 * </div>
 *
 * Button states (via classes on .quick-add__submit):
 *   is-loading  — request in flight: arrow slides left-to-right on loop,
 *                 button turns solid green immediately (optimistic).
 *   is-success  — request confirmed: arrow stops, brief green hold, then
 *                 the button is swapped out for the qty stepper.
 * On failure, both classes are removed and the button returns to normal.
 */
(function () {
  const SUCCESS_HOLD_MS = 350;
  const MIN_LOADING_MS = 300; // avoids an imperceptible flash on fast networks

  function getCartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  document.addEventListener(
    'submit',
    function (event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const wrapper = form.closest('.search-atc-wrapper');
      if (!wrapper) return;

      // Must run before Dawn's own listener (bound directly on this form)
      // ever fires — this is what prevents its redirect-to-cart fallback.
      event.preventDefault();
      event.stopImmediatePropagation();
      handleSubmit(form, wrapper);
    },
    true
  );

  function handleSubmit(form, wrapper) {
    const button = wrapper.querySelector('.quick-add__submit');
    if (!button || button.getAttribute('aria-disabled') === 'true') return;

    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');

    const formData = new FormData(form);
    const variantId = formData.get('id');
    const quantity = formData.get('quantity') || 1;
    const startedAt = Date.now();

    const cartElement = getCartElement();
    if (cartElement && typeof cartElement.getSectionsToRender === 'function') {
      formData.append('sections', cartElement.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      if (typeof cartElement.setActiveElement === 'function') {
        cartElement.setActiveElement(button);
      }
    }

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      },
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status) {
          // Add failed (e.g. went out of stock mid-click) — revert.
          button.classList.remove('is-loading');
          button.removeAttribute('aria-disabled');
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'search-atc-enhance',
            productVariantId: variantId,
            errors: data.errors || data.description,
            message: data.message,
          });
          return;
        }

        // This is the actual call that opens/refreshes the drawer or
        // notification popup — publishing the event alone never does it.
        if (cartElement && typeof cartElement.renderContents === 'function') {
          cartElement.renderContents(data);
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'search-atc-enhance',
          productVariantId: variantId,
          cartData: data,
        });

        const elapsed = Date.now() - startedAt;
        const remaining = Math.max(0, MIN_LOADING_MS - elapsed);

        setTimeout(() => {
          button.classList.remove('is-loading');
          button.classList.add('is-success');

          setTimeout(() => {
            const productForm = wrapper.querySelector('product-form');
            const qtyBox = wrapper.querySelector('.search-atc-qty');
            if (productForm) productForm.hidden = true;
            if (qtyBox) {
              qtyBox.hidden = false;
              const input = qtyBox.querySelector('.search-atc-qty-input');
              if (input) input.value = quantity;
            }
          }, SUCCESS_HOLD_MS);
        }, remaining);
      })
      .catch((error) => {
        console.error(error);
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
      });
  }

  function updateLine(wrapper, variantId, quantity) {
    const cartElement = getCartElement();
    const body = { id: variantId, quantity };

    if (cartElement && typeof cartElement.getSectionsToRender === 'function') {
      body.sections = cartElement.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((cartData) => {
        if (cartElement && typeof cartElement.renderContents === 'function' && cartData.sections) {
          cartElement.renderContents(cartData);
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'search-atc-enhance',
          productVariantId: variantId,
          cartData,
        });

        if (quantity === 0) {
          const qtyBox = wrapper.querySelector('.search-atc-qty');
          const productForm = wrapper.querySelector('product-form');
          const button = wrapper.querySelector('.quick-add__submit');
          if (qtyBox) qtyBox.hidden = true;
          if (productForm) productForm.hidden = false;
          if (button) {
            button.classList.remove('is-loading', 'is-success');
            button.removeAttribute('aria-disabled');
          }
        }
      })
      .catch((error) => console.error(error));
  }

  document.addEventListener('click', function (event) {
    const stepButton = event.target.closest('.search-atc-qty-btn');
    if (!stepButton) return;
    const wrapper = stepButton.closest('.search-atc-wrapper');
    if (!wrapper) return;

    const variantId = wrapper.dataset.searchAtcVariant;
    const qtyBox = wrapper.querySelector('.search-atc-qty');
    const input = qtyBox.querySelector('.search-atc-qty-input');
    const step = parseInt(stepButton.dataset.step, 10);
    const newValue = Math.max(0, parseInt(input.value, 10) + step);
    input.value = newValue;
    updateLine(wrapper, variantId, newValue);
  });

  document.addEventListener('change', function (event) {
    if (!event.target.classList.contains('search-atc-qty-input')) return;
    const wrapper = event.target.closest('.search-atc-wrapper');
    if (!wrapper) return;

    const variantId = wrapper.dataset.searchAtcVariant;
    const newValue = Math.max(0, parseInt(event.target.value, 10) || 0);
    event.target.value = newValue;
    updateLine(wrapper, variantId, newValue);
  });
})();
